import React, { useState, useEffect, useRef } from "react";
import { searchContents, publishContents, updateContent, uploadThumbnail, fetchImage } from '../../api/crawlService';
import ClayManagementToolbar from '@clayui/management-toolbar';
import ClayButton from '@clayui/button';
import { ClayInput } from '@clayui/form';
import ClayModal, { useModal } from '@clayui/modal';
import Pagination from "../../components/Pagination";
import { toast, ToastContainer } from "react-toastify";
import Spinner from 'react-bootstrap/Spinner';
import { format,parseISO } from 'date-fns';

const Newslisting = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [visibleModal, setVisibleModal] = useState(false);
    const [selectedContent, setSelectedContent] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [toastMessage, setToastMessage] = useState("");
    const [showToast, setShowToast] = useState(false);
    const [toastVariant, setToastVariant] = useState("success");
    const [activeTab, setActiveTab] = useState("newsScrapper");

    const searchInputRef = useRef();
    const { observer, onClose } = useModal({
        onClose: () => setVisibleModal(false),
    });

    useEffect(() => {
        const fetchNews = async () => {
            setLoading(true);
            try {
                const searchParams = {
                    keyword: searchQuery,
                    page: currentPage - 1,
                    size: entriesPerPage
                };

                // Apply filters based on the active tab
                if (activeTab === "newsScrapper") {
                    searchParams.status = "NOT_PUBLISHED";
                } else if (activeTab === "galleryNews") {
                    searchParams.newsCategoryList = "GALLERY";
                } else if (activeTab === "highlightList") {
                    searchParams.newsCategoryList = "HIGHLIGHT";
                }

                const data = await searchContents(searchParams);

                // Fetch images for each news item
                const updatedNews = await Promise.all(
                    data.content.map(async (item) => {
                        let imageUrl = null;

                        if (item.thumbnailUrl) {
                            imageUrl = await fetchImage(item.id);
                        }

                        return { ...item, imageUrl };
                    })
                );

                setNews(updatedNews);
                setTotalPages(data.totalPages);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
                searchInputRef.current?.focus();
            }
        };

        if (searchQuery.length === 0 || searchQuery.length >= 3) {
            const debounceFetch = setTimeout(fetchNews, 300);
            return () => clearTimeout(debounceFetch);
        }
    }, [searchQuery, currentPage, entriesPerPage, activeTab]);


    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = parseISO(dateString); // Ensures correct parsing
        return format(date, 'dd/MM/yyyy');
    };

    const getStatusLabel = (status) => {
        return status === "PUBLISHED" ? "Published" : "Not Published";
    };

    const handleViewClick = (content) => {
        setSelectedContent(content);
        setVisibleModal(true);
    };

    const handleCheckboxChange = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter(selectedId => selectedId !== id) : [...prev, id]
        );
    };

    const handleCategoryChange = async (id, category, isChecked) => {
        try {
            setLoading(true);

            // Find and update the selected news item locally
            const updatedNews = news.map((item) => {
                if (item.id === id) {
                    return { ...item, newsCategoryList: isChecked ? category : null };
                }
                return item;
            });

            setNews(updatedNews);

            // Get updated content for API call
            const updatedContent = updatedNews.find((item) => item.id === id);

            // Call API to update news category & status in the backend
            await updateContent({
                id: updatedContent.id,
                newsCategoryList: updatedContent.newsCategoryList,
                status: updatedContent.status
            });

            setToastMessage("Category updated successfully.");
            setToastVariant("success");
            setShowToast(true);
        } catch (err) {
            setToastMessage("Failed to update category.");
            setToastVariant("danger");
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    };

    const handlePublishStatusChange = async (id, isChecked) => {
        try {
            setLoading(true);

            // Find and update the selected news item locally
            const updatedNews = news.map((item) => {
                if (item.id === id) {
                    return { ...item, status: isChecked ? "PUBLISHED" : "NOT_PUBLISHED" };
                }
                return item;
            });

            setNews(updatedNews);

            // Get updated content for API call
            const updatedContent = updatedNews.find((item) => item.id === id);

            // Call API to update news status in the backend
            await updateContent({
                id: updatedContent.id,
                newsCategoryList: updatedContent.newsCategoryList,
                status: updatedContent.status
            });

            setToastMessage("Status updated successfully.");
            setToastVariant("success");
            setShowToast(true);
        } catch (err) {
            setToastMessage("Failed to update status.");
            setToastVariant("danger");
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    };

    const handleThumbnailUpload = async (event, id) => {
        const file = event.target.files[0];
        if (!file) return;
    
        const formData = new FormData();
        formData.append("file", file);
        formData.append("id", id);
    
        try {
            setLoading(true);
    
            // Capture the API response
            const response = await uploadThumbnail(formData, id);
    
            if (response && response.thumbnailUrl) {
                // Fetch the updated image from the server
                const newImageUrl = await fetchImage(id);
    
                // Update the news item with the new image URL
                setNews((prevNews) =>
                    prevNews.map((item) =>
                        item.id === id ? { ...item, imageUrl: newImageUrl } : item
                    )
                );
    
                toast.success("Thumbnail uploaded successfully.");
            } else {
                throw new Error("Invalid response from server");
            }
        } catch (error) {
            console.error("Error uploading thumbnail:", error);
            toast.error("Failed to upload thumbnail.");
        } finally {
            setLoading(false);
        }
    };    

    const handlePublish = async () => {
        if (selectedIds.length === 0) {
            toast.warn("Please select at least one item to publish.");
            return;
        }

        try {
            setLoading(true);
            await publishContents(selectedIds);
            toast.success("Selected items have been published.");
            setSelectedIds([]);
        } catch (err) {
            toast.error("Failed to publish selected items.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <ClayManagementToolbar>
                <ClayManagementToolbar.ItemList expand>
                    <ClayManagementToolbar.Search>
                        <ClayInput.Group>
                            <ClayInput.GroupItem>
                                <ClayInput
                                    type="text"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    ref={searchInputRef}
                                />
                            </ClayInput.GroupItem>
                        </ClayInput.Group>
                    </ClayManagementToolbar.Search>
                </ClayManagementToolbar.ItemList>

                <ClayManagementToolbar.ItemList>
                    <ClayManagementToolbar.Item>
                        <ClayButton displayType="primary" onClick={handlePublish}>
                            Publish
                        </ClayButton>
                    </ClayManagementToolbar.Item>
                </ClayManagementToolbar.ItemList>
            </ClayManagementToolbar>

            {/* Tab Navigation */}
            <div className="mb-4">
                <ClayButton
                    displayType={activeTab === "newsScrapper" ? "primary" : "secondary"}
                    onClick={() => {
                        setActiveTab("newsScrapper");
                        setCurrentPage(1);
                    }}
                >
                    News Scrapper List
                </ClayButton>
                <ClayButton
                    displayType={activeTab === "galleryNews" ? "primary" : "secondary"}
                    onClick={() => {
                        setActiveTab("galleryNews");
                        setCurrentPage(1);
                    }}
                    className="mx-2"
                >
                    Gallery News List
                </ClayButton>
                <ClayButton
                    displayType={activeTab === "highlightList" ? "primary" : "secondary"}
                    onClick={() => {
                        setActiveTab("highlightList");
                        setCurrentPage(1);
                    }}
                >
                    Highlight List
                </ClayButton>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center my-4">
                    <Spinner animation="border" role="status" />
                </div>
            ) : error ? (
                <div className="alert alert-danger">Error loading data</div>
            ) : (
                <>
                    <table className="show-quick-actions-on-hover table table-autofit table-list responsive">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Title</th>
                                <th>Outlet</th>
                                <th>Broadcast Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {news.map((item) => (
                                <tr key={item.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(item.id)}
                                            onChange={() => handleCheckboxChange(item.id)}
                                        />
                                    </td>
                                    <td><a href={item.url} target="new">{item.title}</a></td>
                                    <td>{item.outlet}</td>
                                    <td>{formatDate(item.articleDate || item.createdDate)}</td>
                                    <td>
                                        {activeTab === "newsScrapper" ? (
                                            <>
                                                <label className="mr-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={item.newsCategoryList === "GALLERY"}
                                                        onChange={(e) => handleCategoryChange(item.id, "GALLERY", e.target.checked)}
                                                    />{" "}
                                                    News Gallery
                                                </label>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={item.newsCategoryList === "HIGHLIGHT"}
                                                        onChange={(e) => handleCategoryChange(item.id, "HIGHLIGHT", e.target.checked)}
                                                    />{" "}
                                                    Highlights
                                                </label>
                                            </>
                                        ) : (
                                            <>
                                                {/* Checkbox for Published/Not Published */}
                                                <label className="mr-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={item.status === "PUBLISHED"}
                                                        onChange={(e) => handlePublishStatusChange(item.id, e.target.checked)}
                                                    />{" "}
                                                    Published
                                                </label>
                                                <div className="row">
                                                    <div className="col">
                                                        {/* Upload Thumbnail Button */}
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            style={{ display: "none" }}
                                                            onChange={(e) => handleThumbnailUpload(e, item.id)}
                                                            id={`upload-thumbnail-${item.id}`}
                                                        />
                                                        <label htmlFor={`upload-thumbnail-${item.id}`} className="btn btn-secondary ml-2">
                                                            Upload Thumbnail
                                                        </label>
                                                    </div>
                                                    <div>
                                                        {/* Display uploaded thumbnail if available */}
                                                        {item.imageUrl ? (
                                                            <img src={item.imageUrl} alt="Thumbnail" width="50" height="50" />
                                                        ) : (
                                                            <span>No Image</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        onEntriesChange={setEntriesPerPage}
                        entriesPerPage={entriesPerPage}
                    />
                </>
            )}
            <ToastContainer />
        </div>
    );
};

export default Newslisting;